'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from '@/components/ui/Link'
import { getReadableImageUrl } from '@/lib/directus'
import MobileMenu from './components/MobileMenu'

interface SubmenuItem {
	id: string
	label: string
	href: string
}

interface HeaderProps {
	data: HeaderData
}

export interface HeaderData {
	phone_number: {
		title: string
	}
	phone_icon: {
		id: string
		filename_download: string
	}
	navigation: {
		menu_items: Array<{
			menu_items_id: {
				label: string
				href: string
				submenu: Array<{
					label: string
					href: string
				}>
			}
		}>
	}
	nav_about: {
		id: string
		label: string
		href: string
		submenu: SubmenuItem[]
	}
	nav_gallery: {
		id: string
		label: string
		href: string
		submenu: SubmenuItem[]
	}
	nav_meet: {
		label: string
		href: string
	}
	logo_desktop: {
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
		alt: string
	}
	social_links: {
		external_links_id: {
			url: string
			label: string
			target: string
			icon: {
				id: string
				filename_disk: string
			}
		}
	}[]
}

export const fields = [
	'phone_number.title',
	'phone_icon.id',
	'phone_icon.filename_disk',
	//
	'navigation.menu_items.menu_items_id.label',
	'navigation.menu_items.menu_items_id.href',
	'navigation.menu_items.menu_items_id.submenu.label',
	'navigation.menu_items.menu_items_id.submenu.href',

	'nav_about.id',
	'nav_about.label',
	'nav_about.href',
	'nav_about.submenu.id',
	'nav_about.submenu.label',
	'nav_about.submenu.href',

	'nav_gallery.id',
	'nav_gallery.label',
	'nav_gallery.href',
	'nav_gallery.submenu.id',
	'nav_gallery.submenu.label',
	'nav_gallery.submenu.href',

	'logo_desktop.image_file.image.id',
	'logo_desktop.image_file.image.filename_download',
	'logo_desktop.alt',
	'nav_meet.label',
	'nav_meet.href',

	'social_links.external_links_id.url',
	'social_links.external_links_id.target',
	'social_links.external_links_id.icon.id',
	'social_links.external_links_id.icon.filename_disk',
] as const

export default function Header({ data }: HeaderProps) {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)
	const [activeCategory, setActiveCategory] = useState<string | null>(null)
	const [isMobile, setIsMobile] = useState(false)

	// Check screen size on mount and resize
	useEffect(() => {
		const checkScreenSize = () => {
			setIsMobile(window.innerWidth < 1024)
			if (window.innerWidth < 1024) {
				setIsMegaMenuOpen(false)
			}
		}

		checkScreenSize()
		window.addEventListener('resize', checkScreenSize)
		return () => window.removeEventListener('resize', checkScreenSize)
	}, [isMobile])

	// Prevent body scroll when mobile menu is open
	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
		}

		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isMobileMenuOpen])

	const handleLinkClick = () => {
		setIsMobileMenuOpen(false)
		setIsMegaMenuOpen(false)
	}

	// Transform API data to match the expected structure
	const menuCategories = data?.navigation?.menu_items?.map((item: any) => ({
		label: item.menu_items_id?.label || '',
		href: item.menu_items_id?.href || '',
		submenu: item.menu_items_id?.submenu || []
	})) || []

	// Transform nav_about data for About Buttons Grid
	const aboutButtons: SubmenuItem[] = data?.nav_about?.submenu || []

	// Transform nav_gallery data for Gallery Categories
	const galleryCategories: SubmenuItem[] = data?.nav_gallery?.submenu || []

	return (
		<header className='sticky top-0 z-[8999] w-full'>
			{/* Top Bar with Logo, Doctor Info, Navigation, Social Media, Phone & Menu */}
			<div className='w-full border-t border-b border-gray-200 bg-white'>
				<div className='px-4 sm:px-8 lg:px-16 flex items-center justify-between py-4'>
					{/* Left side - Logo and Doctor Info */}
					<div className='flex items-center gap-4 lg:gap-6'>
						<Link
							href='/'
							className='hover:opacity-80 transition-all duration-300 hover:scale-105'
						>
							<div className='w-[120px] h-[40px] sm:w-[160px] sm:h-[50px] lg:w-[196px] lg:h-[60px] relative'>
								<Image
									src={getReadableImageUrl(
										data?.logo_desktop?.image_file?.image?.id,
										data?.logo_desktop?.image_file?.image?.filename_download
									)}
									alt={data?.logo_desktop?.alt || 'Logo'}
									fill
									className='object-contain'
									priority
								/>
							</div>
						</Link>
					</div>

					{/* Right side - Social Media, Phone & Menu Button */}
					<div className='flex items-center gap-4 lg:gap-[53px]'>
						{/* Social Media Icons - Hidden on mobile, visible on tablet+ */}
						<div className='hidden sm:flex items-center gap-[15px]'>
							{(data?.social_links || []).map(social => (
								<Link
									key={social.external_links_id.url}
									href={social.external_links_id.url}
									target='_blank'
									className='opacity-60 hover:opacity-100 transition-all duration-300 hover:scale-110 hover:rotate-3'
									aria-label={social.external_links_id.label}
								>
									<Image
										src={getReadableImageUrl(
											social.external_links_id.icon.id,
											social.external_links_id.icon.filename_disk
										)}
										alt={social.external_links_id.label || ''}
										width={24}
										height={24}
										className='w-6 h-6 text-[#878787]'
										quality={95}
										loading='lazy'
									/>
								</Link>
							))}
						</div>

						{/* Phone & Menu Button */}
						<div className='flex items-center gap-6'>
							{/* Phone - Hidden on mobile, visible on tablet+ */}
							<div className='hidden sm:flex items-center gap-2 group'>
								{/* Phone Icon */}
								<svg
									className='w-[23px] h-[24px] text-black group-hover:text-blue-600 transition-all duration-300 group-hover:scale-110'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
								</svg>

								{/* Phone Number */}
								<span className='text-lg lg:text-2xl font-aileron text-black leading-[14px] group-hover:text-blue-600 transition-all duration-300'>
									{data?.phone_number?.title || '0208 748 2860'}
								</span>
							</div>

							{/* Mobile Phone Icon - Visible on mobile, positioned closer to menu */}
							<div className='lg:hidden flex items-center gap-2'>
								<Link
									href={`tel:${data?.phone_number?.title || '02045251495'}`}
									className='flex items-center gap-2 hover:opacity-70 transition-opacity'
								>
									<div className='w-8 h-8 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors'>
										<svg
											className='w-4 h-4 text-white'
											viewBox='0 0 24 24'
											fill='currentColor'
										>
											<path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
										</svg>
									</div>
								</Link>
							</div>

							{/* Mobile Menu Button - Visible on mobile */}
							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className='lg:hidden w-8 h-8 flex items-center justify-center relative'
								aria-label='Toggle mobile menu'
							>
								<div className='relative w-6 h-6 transition-all duration-500 top-[2px]'>
									{/* Animated lines that morph into close icon */}
									<span
										className={`absolute top-0 left-0 w-6 h-0.5 bg-black transition-all duration-500 ${
											isMobileMenuOpen
												? 'rotate-45 translate-y-2'
												: 'rotate-0 translate-y-0'
										}`}
									></span>
									<span
										className={`absolute top-2 left-0 w-6 h-0.5 bg-black transition-all duration-500 ${
											isMobileMenuOpen
												? 'opacity-0 scale-0'
												: 'opacity-100 scale-100'
										}`}
									></span>
									<span
										className={`absolute top-4 left-0 w-6 h-0.5 bg-black transition-all duration-500 ${
											isMobileMenuOpen
												? '-rotate-45 -translate-y-2'
												: 'rotate-0 translate-y-0'
										}`}
									></span>
								</div>
							</button>

							{/* Desktop Menu Button - Hidden on mobile, visible on desktop */}
							<button
								onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}
								className='hidden lg:block w-8 h-8 flex items-center justify-center relative'
								aria-label='Toggle mega menu'
							>
								<div className='relative w-6 h-6 transition-all duration-500 top-[2px]'>
									{/* Animated lines that morph into close icon */}
									<span
										className={`absolute top-0 left-0 w-6 h-0.5 bg-black transition-all duration-500 ${
											isMegaMenuOpen
												? 'rotate-45 translate-y-2'
												: 'rotate-0 translate-y-0'
										}`}
									></span>
									<span
										className={`absolute top-2 left-0 w-6 h-0.5 bg-black transition-all duration-500 ${
											isMegaMenuOpen
												? 'opacity-0 scale-0'
												: 'opacity-100 scale-100'
										}`}
									></span>
									<span
										className={`absolute top-4 left-0 w-6 h-0.5 bg-black transition-all duration-500 ${
											isMegaMenuOpen
												? '-rotate-45 -translate-y-2'
												: 'rotate-0 translate-y-0'
										}`}
									></span>
								</div>
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Mega Menu - Hidden on mobile */}
			{isMegaMenuOpen && !isMobile && (
				<div className='hidden lg:block fixed top-[88px] left-0 right-0 bottom-0 z-[9000] bg-white'>
					{/* Mega Menu Content */}
					<div className='flex h-full'>
						{/* Left Sidebar - About Dr. De Silva */}
						<div className='w-[415px] 2xl:w-[490px] ps-16 2xl:pe-16 py-8 flex flex-col gap-6'>
							{/* Meet Dr. De Silva Section */}
							<Link
								href={
									data?.nav_meet?.href || '/about/dr-julian-de-silva-mbbs-md/'
								}
								className='flex gap-4 group'
								onClick={() => setIsMegaMenuOpen(false)}
							>
								<div className='w-[161px] h-[139px] relative overflow-hidden group'>
									<Image
										src='/images/nav-about-img2.webp'
										alt='Dr. Julian De Silva'
										fill
										className='object-cover transition-all duration-500 group-hover:scale-110 group-hover:rotate-1'
										priority
									/>
									<div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300'></div>
								</div>
								<div className='flex flex-col gap-2'>
									<h3 className='text-xl font-aileron text-[#030303] uppercase leading-7 tracking-[1px] font-bold'>
										MEET
										<br />
										{data?.nav_meet?.label || 'DR. DE SILVA'}
									</h3>
									{/* Arrow Icon */}
									<div className='mt-2 group-hover:translate-x-1 transition-transform duration-300'>
										<svg
											width='24'
											height='24'
											viewBox='0 0 24 24'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path
												d='M5 12H19M19 12L12 5M19 12L12 19'
												stroke='black'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>
									</div>
								</div>
							</Link>

							{/* About Buttons Grid */}
							<div className='grid grid-cols-2 gap-3'>
								{aboutButtons.map((button, index) => (
									<Link
										key={button.href}
										href={button.href}
										className={`px-6 py-3 bg-white border border-[#B2B2B2] rounded-full text-center text-xs font-jakarta font-bold uppercase text-[#404040] leading-[20.4px] tracking-[1.2px] hover:bg-gray-50 hover:border-black hover:shadow-lg hover:scale-105 transition-all duration-300 flex justify-center items-center ${
											// Make MEDICAL PUBLICATIONS span both columns (index 6)
											index === 6 ? 'col-span-2' : ''
										}`}
										onClick={() => setIsMegaMenuOpen(false)}
									>
										{button.label}
									</Link>
								))}
							</div>
						</div>

						{/* Center - Services Menu */}
						<div className='flex-1 flex flex-col justify-between'>
							<div className='flex gap-[27px] px-8 py-8'>
								{/* Main Categories */}
								<div className='w-[225px] 2xl:w-[275px]'>
									<div className='flex flex-col gap-4'>
										{menuCategories.map(category => (
											<button
												key={category.href}
												onClick={() =>
													setActiveCategory(
														activeCategory === category.label
															? null
															: category.label
													)
												}
												className={`w-full px-1 py-2 text-lg 2xl:text-xl font-aileron text-[#030303] uppercase leading-7 border-b border-black transition-all duration-300 text-left hover:bg-black hover:pl-3 hover:text-white ${
													activeCategory === category.label
														? 'bg-black text-white pl-3'
														: ''
												}`}
											>
												{category.label}
											</button>
										))}
									</div>
								</div>

								{/* Submenu Items - positioned to align with active category */}
								{activeCategory && (
									<div className='flex-1'>
										<div
											className='flex flex-col gap-3'
											style={{
												marginTop: `${
													menuCategories.findIndex(
														cat => cat.label === activeCategory
													) * 65
												}px`,
											}}
										>
											{menuCategories
												.find(cat => cat.label === activeCategory)
												?.submenu?.map(item => (
													<Link
														key={item.href}
														href={item.href}
														className='px-1 py-2 text-lg font-aileron text-black leading-6 hover:font-bold hover:pl-3 transition-all duration-300'
														onClick={() => setIsMegaMenuOpen(false)}
													>
														{item.label}
													</Link>
												))}
										</div>
									</div>
								)}
							</div>

							{/* Book Consultation Button */}
							<div className='px-8 pb-8 flex justify-center'>
								<Link
									href='/contact-us/'
									className='w-[320px] px-9 py-[17px] bg-black border border-[#B2B2B2] rounded-full text-center text-xs font-jakarta font-bold uppercase text-white leading-[20.4px] tracking-[1.2px] hover:bg-gray-800 hover:scale-105 hover:shadow-xl transition-all duration-300'
									onClick={() => setIsMegaMenuOpen(false)}
								>
									Book your consultation
								</Link>
							</div>
						</div>

						{/* Right Sidebar - Before & After Gallery */}
						<div className='w-[490px] bg-black border-l border-black px-8 py-8 flex flex-col gap-4'>
							<h2 className='2xl:text-5xl text-3xl font-freightBig text-white text-center font-light leading-[48px] mb-4'>
								{data?.nav_gallery?.label || 'Before & After Gallery'}
							</h2>

							{/* Gallery Image */}
							<Link
								href='/gallery/'
								className='w-full h-[240px] relative overflow-hidden group block'
							>
								<Image
									src='/images/nav-ba-img.webp'
									alt='Before & After Photo'
									fill
									className='object-cover object-top transition-all duration-500 group-hover:scale-110 group-hover:rotate-1'
									priority
								/>
								<div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300'></div>
							</Link>

							{/* Gallery Filter Buttons */}
							<div className='grid gap-4'>
								{galleryCategories.map(category => (
									<Link
										key={category.href}
										href={category.href}
										className='text-lg 2xl:text-xl font-aileron text-center uppercase text-white '
										onClick={() => setIsMegaMenuOpen(false)}
									>
										{category.label}
									</Link>
								))}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Mobile Menu */}
			<MobileMenu
				isOpen={isMobileMenuOpen}
				data={data}
				onClose={() => setIsMobileMenuOpen(false)}
				onLinkClick={handleLinkClick}
				menuCategories={menuCategories}
				aboutButtons={aboutButtons}
				galleryCategories={galleryCategories}
			/>
		</header>
	)
}
